import { Pricing, Product } from '@leid/api-products'
import { tryParseAsNumber } from '@leid/helpers'
import { round, roundUp } from 'utils/removeDecimals'

export const workingDayHours = 6

export enum PricingPeriodType {
  HOURLY = 'HOURLY',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export type PricingInfo = {
  type: PricingPeriodType

  price: number
  possiblePrice: number | undefined
  profitPercentage: number | undefined
}

export type ParsedPricingInfo = {
  minimalPrice: number
  priceGroupId: number | undefined

  handlingFee: number | undefined
  insurancePercentage: number | undefined
  insurance: number | undefined

  deductiblesAmount: number | undefined
  deductiblesPrice: number | undefined

  periodsInfo: PricingInfo[]
}

export type ProductPricingInfo = {
  minimalPrice: number
  price: number | undefined
  priceType: PricingPeriodType
  periodsInfo: PricingInfo[]
}

export const parseProductPricingInfo = (product: Product, pricing: Pricing): ProductPricingInfo => {
  const minimalPrice = getMinimalPrice(pricing)

  const periodsInfo = getPeriodsInfo(pricing)

  let usedPeriod:
    | {
        price: number | undefined
        priceType: PricingPeriodType
      }
    | undefined

  const parsedRentalTime = tryParseAsNumber(product.averageRentalTime)
  if (parsedRentalTime === undefined) {
    usedPeriod = getShortesPricingPeriod(periodsInfo)
  } else {
    usedPeriod = getPricingPeriodForAverageRentTime(parsedRentalTime, periodsInfo)
  }

  return {
    minimalPrice: minimalPrice,
    price: usedPeriod?.price,
    priceType: usedPeriod?.priceType ?? PricingPeriodType.HOURLY,
    periodsInfo,
  }
}

export const parsePricingInfo = (pricing: Pricing): ParsedPricingInfo => {
  const minimalPrice = getMinimalPrice(pricing)

  const periodsInfo: PricingInfo[] = []
  // Hourly
  const hourlyPeriod = getPeriodInfo(PricingPeriodType.HOURLY, pricing, undefined)
  if (hourlyPeriod) {
    periodsInfo.push(hourlyPeriod)
  }
  // Daily
  const dailyPeriod = getPeriodInfo(PricingPeriodType.DAILY, pricing, hourlyPeriod)
  if (dailyPeriod) {
    periodsInfo.push(dailyPeriod)
  }
  //Weekly
  const weeklyPeriod = getPeriodInfo(PricingPeriodType.WEEKLY, pricing, dailyPeriod)
  if (weeklyPeriod) {
    periodsInfo.push(weeklyPeriod)
  }

  const result: ParsedPricingInfo = {
    priceGroupId: pricing.priceGroupId,
    minimalPrice,
    handlingFee: pricing.handlingFee,
    insurance: pricing.insurancePrice,
    insurancePercentage: pricing.insurancePercentage,
    periodsInfo,
    deductiblesAmount: pricing.deductible,
    deductiblesPrice: pricing.reductionOfDeductible,
  }
  return result
}

export const getMinimalPeriod = (pricingPeriods: PricingInfo[] | undefined): PricingInfo | undefined => {
  if (!pricingPeriods) {
    return undefined
  }
  const hourly = getPeriodByType(PricingPeriodType.HOURLY, pricingPeriods)
  if (hourly) {
    return hourly
  }
  const daily = getPeriodByType(PricingPeriodType.DAILY, pricingPeriods)
  if (daily) {
    return daily
  }
  const weekly = getPeriodByType(PricingPeriodType.WEEKLY, pricingPeriods)
  if (weekly) {
    return weekly
  }
  return undefined
}

const getPeriodByType = (type: PricingPeriodType, pricingPeriods: PricingInfo[]): PricingInfo | undefined => {
  const period = pricingPeriods.find((s) => s.type === type)
  return period
}

const getPeriodInfo = (
  period: PricingPeriodType,
  pricing: Pricing,
  prevPeriod?: PricingInfo,
): PricingInfo | undefined => {
  const periodPrice = getPeriodPrice(period, pricing)
  if (periodPrice === undefined) {
    return undefined
  }

  const insurance = pricing.insurancePercentage
    ? calculateInsurancePercentage(periodPrice, pricing.insurancePercentage)
    : undefined

  const fullPrice = round(periodPrice + (insurance ?? 0), 0)

  let profit: number | undefined
  let fullPosiblePrice: number | undefined
  if (prevPeriod) {
    const prevPeriodsAmount = getPrevPeriodPeriodsAmount(prevPeriod.type)
    if (prevPeriodsAmount !== undefined) {
      fullPosiblePrice = round(prevPeriod.price * prevPeriodsAmount, 0)
      profit = round(((fullPosiblePrice - fullPrice) / fullPosiblePrice) * 100, 0)
    }
  }

  const result: PricingInfo = {
    type: period,
    price: fullPrice,
    possiblePrice: fullPosiblePrice,
    profitPercentage: profit,
  }
  return result
}

const getMinimalPrice = (pricing: Pricing): number => {
  let minimalRentalPrice =
    pricing.hourly ?? pricing.daily ?? pricing.weekly ?? pricing.monthly ?? pricing.overMonthly ?? 0

  if (pricing.minimalRentalPrice && pricing.minimalRentalPrice > minimalRentalPrice) {
    minimalRentalPrice = pricing.minimalRentalPrice
  }

  let insurancePrice = 0
  if (pricing.insurancePercentage) {
    insurancePrice = roundUp((minimalRentalPrice * pricing.insurancePercentage) / 100, 0)
    if (pricing.insurancePrice && pricing.insurancePrice > insurancePrice) {
      insurancePrice = pricing.insurancePrice
    }
  } else {
    insurancePrice = pricing.insurancePrice ?? 0
  }

  minimalRentalPrice = round(minimalRentalPrice + insurancePrice, 2)

  const roundedResult = round(minimalRentalPrice, 0)
  return roundedResult
}

const getPeriodPrice = (period: PricingPeriodType, pricing: Pricing): number | undefined => {
  switch (period) {
    case PricingPeriodType.HOURLY:
      return pricing.hourly
    case PricingPeriodType.DAILY:
      return pricing.daily
    case PricingPeriodType.WEEKLY:
      return pricing.weekly
    default:
      return undefined
  }
}

const getPrevPeriodPeriodsAmount = (period: PricingPeriodType): number | undefined => {
  switch (period) {
    case PricingPeriodType.HOURLY:
      return 24
    case PricingPeriodType.DAILY:
      return 7
    case PricingPeriodType.WEEKLY:
      return undefined
    default:
      return undefined
  }
}

const calculateInsurancePercentage = (price: number, insurancePercentage: number) => {
  const insurancePrice = roundUp((price * insurancePercentage) / 100, 0)
  return insurancePrice
}

const getPeriodsInfo = (pricing: Pricing): PricingInfo[] => {
  const result: PricingInfo[] = []
  const hourlyPeriod = getPeriodInfo(PricingPeriodType.HOURLY, pricing)
  if (hourlyPeriod) {
    result.push(hourlyPeriod)
  }
  const dailyPeriod = getPeriodInfo(PricingPeriodType.DAILY, pricing)
  if (dailyPeriod) {
    result.push(dailyPeriod)
  }
  const weeklyPeriod = getPeriodInfo(PricingPeriodType.WEEKLY, pricing)
  if (weeklyPeriod) {
    result.push(weeklyPeriod)
  }
  const monthlyPeriod = getPeriodInfo(PricingPeriodType.MONTHLY, pricing)
  if (monthlyPeriod) {
    result.push(monthlyPeriod)
  }
  return result
}

const getShortesPricingPeriod = (
  periodsInfo: PricingInfo[],
): { price: number | undefined; priceType: PricingPeriodType } | undefined => {
  const hourlyPeriod = periodsInfo.find((s) => s.type === PricingPeriodType.HOURLY)
  if (hourlyPeriod) {
    return { price: hourlyPeriod.price, priceType: hourlyPeriod.type }
  }

  const dailyPeriod = periodsInfo.find((s) => s.type === PricingPeriodType.DAILY)
  if (dailyPeriod) {
    return { price: dailyPeriod.price, priceType: dailyPeriod.type }
  }

  const weeklyPeriod = periodsInfo.find((s) => s.type === PricingPeriodType.WEEKLY)
  if (weeklyPeriod) {
    return { price: weeklyPeriod.price, priceType: weeklyPeriod.type }
  }

  return undefined
}

const getPricingPeriodForAverageRentTime = (
  averageRentTime: number,
  periodsInfo: PricingInfo[],
): { price: number | undefined; priceType: PricingPeriodType } | undefined => {
  if (averageRentTime <= 1) {
    return getPeriodInfoByType(PricingPeriodType.HOURLY, periodsInfo)
  }

  if (averageRentTime < 7) {
    return getPeriodInfoByType(PricingPeriodType.DAILY, periodsInfo)
  }

  return getPeriodInfoByType(PricingPeriodType.WEEKLY, periodsInfo)
}

const getPeriodInfoByType = (
  type: PricingPeriodType,
  periodsInfo: PricingInfo[],
): { price: number | undefined; priceType: PricingPeriodType } | undefined => {
  const periodInfo = periodsInfo.find((s) => s.type === type)
  if (periodInfo) {
    return {
      price: periodInfo.price,
      priceType: periodInfo.type,
    }
  }

  const shortest = getShortesPricingPeriod(periodsInfo)
  if (shortest) {
    return {
      price: shortest.price,
      priceType: shortest.priceType,
    }
  }

  return undefined
}
